import React, { useState } from 'react';
import { Formik } from 'formik';

// Data
import { IUpdateAssessmentContainer, IUpdateAssessmentData } from './update-assessment.interface';
import { canEditAttendanceDefault } from '../../Attendance/attendance.permissions';
import { useCompanyHasReportCards } from '../../../lib/stores/company.store';

// Components
import { AssessmentInfoForm } from '../CreateAssessment/assessment-info-form.component';
import { validationSchema } from '../CreateAssessment/assessment-info-form.validation';
import { ICreateAssessmentFields } from '../CreateAssessment/create-assessment.interface';
import { isNaN } from 'lodash';
import { ASSESSMENT_DESCRIPTOR_OPTIONS, SEASON_OPTIONS } from '../CreateAssessment/create-assessment.constants';
import { ISelectOption } from '../CreateAssessment/create-assessment.types';

export const UpdateAssessmentContainer: React.FC<IUpdateAssessmentContainer> = ({
  updateAssessment,
  assessment,
  company
}) => {
  const [showSaved, setShowSaved] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Render and data visibility stay aligned: each checkbox is render-gated on
  // these checks, so its value must not ride along in the payload without them.
  const userCanEditAttendanceDefault = canEditAttendanceDefault();
  const hasReportCards = useCompanyHasReportCards();
  const canEditReportCardDefault = userCanEditAttendanceDefault && hasReportCards;

  // Seed the RAW tri-state values, never collapsed to `false`. See "The
  // assessment-level default is tri-state in the edit form" in CLAUDE.md.
  const assessmentAttendanceDefault = assessment?.isAttendanceRequestedDefault ?? null;
  const assessmentIncludeNonConcludedDefault = assessment?.reportCardsIncludeNonConcludedDefault ?? null;
  const assessmentRetainJerseyAssignments = assessment?.retainJerseyAssignments ?? null;

  const splitName = assessment?.name.split(" ")

  const year = splitName?.length
    ? isNaN(splitName[0]) ? new Date().getFullYear() : parseInt(splitName[0])
    : new Date().getFullYear()

  const season = splitName?.length && splitName.length > 1
    ? SEASON_OPTIONS.find(so => so.value === splitName[1]) ? {value: splitName[1], label: splitName[1]} : SEASON_OPTIONS[Math.floor(new Date().getMonth() / 3)]
    : SEASON_OPTIONS[Math.floor(new Date().getMonth() / 3)]

  const descriptor = splitName?.length && splitName.length > 2
    ? {
        value: splitName[2],
        label: splitName[2],
        __isNew__: ASSESSMENT_DESCRIPTOR_OPTIONS.find(so => so.value === splitName[2]) ? true : undefined
      } as ISelectOption
    : ASSESSMENT_DESCRIPTOR_OPTIONS[0]

  const additionalContext = splitName?.length && splitName.length > 3
    ? splitName.slice(3).join(" ")
    : ''

  const INITIAL_FORM_VALUES: Partial<ICreateAssessmentFields> = {
    year,
    season,
    descriptor,
    additionalContext,
    infoLink: assessment?.infoLink || '',
    evaluationGroupName: assessment?.evaluationGroupName || company?.evaluationGroupName || '',
    introLink: assessment?.introLink || company?.introLink || '',
    isAttendanceRequestedDefault: assessmentAttendanceDefault,
    reportCardsIncludeNonConcludedDefault: assessmentIncludeNonConcludedDefault,
    retainJerseyAssignments: assessmentRetainJerseyAssignments,
  };

  async function handleSubmit({year, season, descriptor, additionalContext, infoLink, concludeEmailTemplate, evaluationGroupName, introLink, isAttendanceRequestedDefault, reportCardsIncludeNonConcludedDefault, retainJerseyAssignments}: Partial<ICreateAssessmentFields>, { setSubmitting }: any) {
    setSubmitError(null);

    const data: IUpdateAssessmentData = {
      infoLink,
      concludeEmailTemplate,
      evaluationGroupName,
      introLink,
      name: year && season && descriptor
        ? [year, season.value, descriptor.value, additionalContext].filter(v => v).join(" ")
        : undefined
    };

    if (
      userCanEditAttendanceDefault &&
      typeof isAttendanceRequestedDefault === 'boolean' &&
      isAttendanceRequestedDefault !== assessmentAttendanceDefault
    ) {
      data.isAttendanceRequestedDefault = isAttendanceRequestedDefault;
    }

    if (
      canEditReportCardDefault &&
      typeof reportCardsIncludeNonConcludedDefault === 'boolean' &&
      reportCardsIncludeNonConcludedDefault !== assessmentIncludeNonConcludedDefault
    ) {
      data.reportCardsIncludeNonConcludedDefault = reportCardsIncludeNonConcludedDefault;
    }

    // Same tri-state rule as the RSVP default: send only a concrete choice that differs from what's stored.
    if (
      userCanEditAttendanceDefault &&
      typeof retainJerseyAssignments === 'boolean' &&
      retainJerseyAssignments !== assessmentRetainJerseyAssignments
    ) {
      data.retainJerseyAssignments = retainJerseyAssignments;
    }

    updateAssessment(data)
      .then(() => {
        setShowSaved(true);
        setTimeout(() => {
          setShowSaved(false);
        }, 3000);
      })
      .catch(error => {
        console.error('updateAssessment failed:', error);
        setSubmitError("Couldn't save the assessment. Please try again.");
      })
      .finally(() => setSubmitting(false));
  }

  return (
    <Formik
      enableReinitialize
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => <AssessmentInfoForm form={form} submitError={submitError || undefined} showSaved={showSaved} company={company} update canEditAttendanceDefault={userCanEditAttendanceDefault} canEditReportCardDefault={canEditReportCardDefault} />}
    </Formik>
  );
};

